import React, { useEffect, useState } from 'react';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import color from 'src/utils/colors';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { StyledSettingInput } from '../components/StyledSettingInput';
import { SubmitHandler, useWatch, useFormContext, Controller, FieldPath } from 'react-hook-form';
import { Box, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { RequirementsModal } from '../components/RequirementsModal';
import { DropdownMenu } from '../components/StyledDropdown';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import {
  FinalFormProfile,
  FinalFormProfileSchema,
  VARIANT
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/types/profileTypes';

import {
  useCreateProfile,
  useUpdateProfile
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/useProfiles';
import { arrayToOptions } from './utils';
import { StyledMultilineField } from './components/StyledMultilineField';
import { KeywordsSection } from './components/KeywordsSection';
import { DominantSections } from './DominantSections';
import { VoiceDefinitionsPopup } from './components/VoiceDefinitionsPopup';
import { ToneDefinitionsPopup } from './components/ToneDefinitionsPopup';
import { apiArchiveProfile } from '../api/profileApis';
import { useSnackbar } from 'src/utils/Hooks';
import { useQueryClient } from '@tanstack/react-query';
import { mapToAddFormData } from '../hooks/dtoMappers';
import { ProfileRequirementsSection } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/components/ProfileRequirementsSection';

export const StyledDivider = styled(Divider)({
  margin: '32px 0'
});

// Dropdown options for content limits - using arrays like DominantSections
const bulletCountValues = ['5', '6', '7', '8', '9', '10'] as const;
const titleLengthValues = ['80', '100', '120', '150', '180', '200'] as const;
const bulletLengthValues = ['100', '125', '150', '175', '200', '225', '250'] as const;
const descriptionLengthValues = ['1000', '1200', '1400', '1600', '1800', '2000'] as const;

interface NumericLabelledDropdownProps {
  options: Array<{ id: string; label: string }>;
  label: string;
  name: FieldPath<FinalFormProfile>;
  defaultNumericValue?: number;
  disabled?: boolean;
}

// Numeric dropdown that converts string option.id to number for form
const NumericLabelledDropdown = ({
  options,
  label,
  name,
  defaultNumericValue,
  disabled
}: NumericLabelledDropdownProps) => {
  const { control } = useFormContext<FinalFormProfile>();
  const defaultValue = defaultNumericValue?.toString();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => (
        <Flex flexDirection="column" gap={8}>
          <Text variant="h7" sx={{ fontWeight: 500 }}>
            {label}
          </Text>
          <DropdownMenu
            defaultValue={defaultValue}
            options={options}
            selectedId={value?.toString() || defaultValue}
            disabled={disabled}
            stacklineButtonProps={{
              buttonSx: {
                width: disabled ? 'fit-content' : '82px',
                minWidth: 'unset',
                justifyContent: 'space-between',
                paddingLeft: '12px'
              }
            }}
            onChange={(option: { id: string; label: string }) => {
              // Convert string id to integer with base 10, fallback to 0 if invalid
              const numericValue = parseInt(option.id, 10);
              if (!isNaN(numericValue)) {
                onChange(numericValue); // Only set the numeric value, not the entire object
              } else {
                console.warn(`Invalid numeric value for ${name}: ${option.id}`);
                onChange(0);
              }
            }}
          />
        </Flex>
      )}
    />
  );
};

const ProfileForm = ({
  onBack,
  onSuccess,
  readOnly = false
}: {
  redirectUrl?: string;
  onBack?: () => void;
  onSuccess?: () => void;
  readOnly?: boolean;
}) => {
  const {
    control,
    handleSubmit,
    formState: { isValid }
  } = useFormContext<FinalFormProfile>();
  const variant = useWatch({ control, name: VARIANT });
  const formValues = useWatch({ control });
  const aiInstructions = useWatch({
    control,
    name: 'settings.aiInstructions' as FieldPath<FinalFormProfile>
  });
  const keywordInclusion = useWatch({
    control,
    name: 'settings.keywords.inclusion' as FieldPath<FinalFormProfile>
  });
  const keywordExclusion = useWatch({
    control,
    name: 'settings.keywords.exclusion' as FieldPath<FinalFormProfile>
  });
  const guidelines = useWatch({
    control,
    name: 'settings.guidelines' as FieldPath<FinalFormProfile>
  });

  const hasNonWhitespaceText = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;
  const hasArrayItems = (value: unknown): value is unknown[] => Array.isArray(value) && value.length > 0;

  // In readOnly mode, hide empty sections entirely (per mock annotations).
  const shouldShowAiInstructionsSection = !readOnly || hasNonWhitespaceText(aiInstructions);
  const shouldShowKeywordsSection = !readOnly || hasArrayItems(keywordInclusion) || hasArrayItems(keywordExclusion);
  const shouldShowRequirementsSection = !readOnly || hasArrayItems(guidelines);
  const shouldShowPostToneDivider = shouldShowKeywordsSection || shouldShowRequirementsSection;
  const shouldShowDividerBetweenKeywordsAndRequirements = shouldShowKeywordsSection && shouldShowRequirementsSection;

  const { createProfileAsync } = useCreateProfile();
  const { updateProfileAsync } = useUpdateProfile();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isArchiving, setIsArchiving] = useState(false);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [toneModalOpen, setToneModalOpen] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);

  // Tracking the validity of the form, so we can enable/disable the save button
  // use this for debugging only
  useEffect(() => {
    const result = FinalFormProfileSchema.safeParse(formValues);
    console.log('Form validation result:', {
      success: result.success,
      errors: result.success ? null : result.error.issues,
      formValues
    });
  }, [formValues]);

  /**
   * This function will not be called if the form is not valid
   *  */
  const rhfOnSubmit: SubmitHandler<FinalFormProfile> = async (fullProfile) => {
    try {
      if (variant === 'add') {
        await createProfileAsync(fullProfile);
      } else {
        await updateProfileAsync(fullProfile);
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Profile save failed:', error);
    }
  };

  const handleBackClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // Use callback if provided, otherwise use browser back
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  const handleArchive = (e: React.MouseEvent) => {
    e.preventDefault();

    // Only allow archiving in edit mode when we have a profileId
    if (variant !== 'edit' || !formValues || formValues.variant !== 'edit') {
      console.warn('Cannot archive: not in edit mode or missing profileId');
      return;
    }

    // Show confirmation dialog
    setIsArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    // Type guard to ensure we have profileId
    if (formValues.variant !== 'edit') {
      console.error('Cannot archive: not in edit mode');
      return;
    }

    try {
      setIsArchiving(true);
      setIsArchiveDialogOpen(false);
      await apiArchiveProfile(formValues.profileId);
      showSnackbar({
        message: 'Profile archived successfully.',
        type: 'success'
      });

      // Invalidate profiles queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['Profiles'] });

      // Navigate back after successful archive
      if (onBack) {
        onBack();
      }
    } catch (error) {
      showSnackbar({
        message: 'Failed to archive profile.',
        type: 'error'
      });
    } finally {
      setIsArchiving(false);
    }
  };

  const handleArchiveCancel = () => {
    setIsArchiveDialogOpen(false);
  };

  return (
    <Box
      component={readOnly ? 'div' : 'form'}
      onSubmit={readOnly ? undefined : handleSubmit(rhfOnSubmit)}
      sx={{
        marginBottom: '48px'
      }}
    >
      <Flex flexDirection="column">
        {/* Profile Name */}
        <Flex flexDirection="column" sx={{ marginTop: '32px' }}>
          <Flex justifyContent="space-between" alignItems="center">
            <Text sx={{ fontWeight: 700, fontSize: '14px', letterSpacing: '0.08px', lineHeight: '20px' }}>
              Profile Name
            </Text>
          </Flex>
          <StyledSettingInput<FinalFormProfile>
            textSx={{ display: 'none' }}
            placeholder="Name your content profile"
            name="profileName"
            height="40px"
            characterLimit={100}
            defaultValue={formValues?.profileName}
            disabled={readOnly}
          />
        </Flex>

        <StyledDivider />

        {/* Content Limits */}
        <Flex flexDirection="column">
          <Text sx={{ fontWeight: 700, fontSize: '16px', letterSpacing: '0.08px', lineHeight: '24px' }}>
            Generated Content Character Limit
          </Text>
          <Text
            variant="body1"
            sx={{
              marginTop: '4px'
            }}
          >
            Set character limit and bullet count to control the amount of content generated per SKU.
          </Text>
          <Flex gap={48} sx={{ marginTop: '16px' }}>
            <NumericLabelledDropdown
              options={arrayToOptions(titleLengthValues)}
              label="Title Limit"
              name="settings.characterLimits.titles"
              disabled={readOnly}
            />
            <NumericLabelledDropdown
              options={arrayToOptions(bulletCountValues)}
              label="Bullet Count"
              name="settings.bulletPointsCount"
              disabled={readOnly}
            />
            <NumericLabelledDropdown
              options={arrayToOptions(bulletLengthValues)}
              label="Bullet Limit"
              name="settings.characterLimits.bullets"
              disabled={readOnly}
            />
            <NumericLabelledDropdown
              options={arrayToOptions(descriptionLengthValues)}
              label="Description Limit"
              name="settings.characterLimits.descriptions"
              disabled={readOnly}
            />
          </Flex>
        </Flex>

        {/* AI Content */}
        {shouldShowAiInstructionsSection && (
          <Flex flexDirection="column" sx={{ marginTop: '48px' }}>
            <Text sx={{ fontWeight: 700, fontSize: '16px', letterSpacing: '0.08px', lineHeight: '24px' }}>
              AI Custom Instructions
            </Text>
            <Text
              variant="body1"
              sx={{
                marginTop: '4px'
              }}
            >
              Add any brand, style, or content guidelines you want the AI to follow when generating new content.
            </Text>
            <Flex sx={{ marginTop: '16px', width: '100%' }}>
              <StyledMultilineField
                name="settings.aiInstructions"
                label=""
                placeholder="Enter any specific instructions you would like the AI to follow"
                rows={4}
                sx={{
                  '& .MuiInputBase-input': {
                    height: '122px !important'
                  }
                }}
                disabled={readOnly}
              />
            </Flex>
          </Flex>
        )}

        {/* Dominant Voice & Tone */}
        <Flex sx={{ marginTop: '48px' }}>
          <DominantSections<FinalFormProfile>
            voiceFieldName="settings.voice.type"
            toneFieldName="settings.tone.type"
            readOnly={readOnly}
            onVoiceTooltipClick={() => setVoiceModalOpen(true)}
            onToneTooltipClick={() => setToneModalOpen(true)}
          />
        </Flex>

        {shouldShowPostToneDivider && <StyledDivider />}

        {/* Keywords */}
        {shouldShowKeywordsSection && (
          <KeywordsSection<FinalFormProfile>
            inclusionFieldName="settings.keywords.inclusion"
            exclusionFieldName="settings.keywords.exclusion"
            readOnly={readOnly}
          />
        )}

        {shouldShowDividerBetweenKeywordsAndRequirements && <StyledDivider />}

        {/* Guidelines/Requirements */}
        {shouldShowRequirementsSection && <ProfileRequirementsSection<FinalFormProfile> />}

        <Flex
          justifyContent="space-between"
          sx={{
            marginTop: '64px'
          }}
        >
          <SlButton
            buttonSx={{
              border: `1px solid ${color.graysSecondary}`
            }}
            onClick={handleBackClick}
          >
            Cancel
          </SlButton>
          {!readOnly && (
            <Flex gap={16}>
              <SlButton variant="contained" type="submit" disabled={!isValid}>
                Save
              </SlButton>
            </Flex>
          )}
        </Flex>
        {!readOnly && <RequirementsModal<FinalFormProfile> />}
        <VoiceDefinitionsPopup open={voiceModalOpen} onClose={() => setVoiceModalOpen(false)} />
        <ToneDefinitionsPopup open={toneModalOpen} onClose={() => setToneModalOpen(false)} />
        <ConfirmationDialog
          open={isArchiveDialogOpen}
          onClose={handleArchiveCancel}
          onConfirm={handleArchiveConfirm}
          title="Archive Content Profile"
          message="Are you sure you want to archive this content profile? Once this profile is archived, it cannot be recovered."
          confirmButtonText="Archive"
          cancelButtonText="Cancel"
          isLoading={isArchiving}
        />
      </Flex>
    </Box>
  );
};

export default ProfileForm;
